import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { Dropdown } from '@loadsmart/loadsmart-ui'
import { Layout } from '@loadsmart/miranda-react'

import IconGoogleSheets from 'core/ui/assets/icon-google-sheets.svg'
import { Icon } from 'core/ui/components/icon/Icon'

import { type DistanceUnit } from '../domain/DistanceUnit'
import { type RateType } from '../domain/RateType'
import { useUploadRates } from './hooks/useUploadRates'
import { UploadRfpLanesDialog } from './UploadRfpLanesDialog'

export type RfpRatesButtonProps = {
  distanceUnit: DistanceUnit
  isUploadDisabled?: boolean
  rfpId: string
  rfpName: string
  rateType: RateType
  onFileUploaded: () => void
  setUploadTimeoutError: () => void
}

const UPLOAD_TIMEOUT_IN_MS = 30000

export function RfpRatesButton({
  distanceUnit,
  isUploadDisabled = false,
  rfpId,
  rfpName,
  rateType,
  onFileUploaded,
  setUploadTimeoutError,
}: RfpRatesButtonProps) {
  const { downloadRatesTemplate, uploadRates, isUploading } = useUploadRates()

  const [isUploadPricesFileDialogOpened, setIsUploadPricesFileDialogOpened] = useState(false)

  const handleOnUploadConfirm = useCallback(
    async (file: File, selectedRateType: string) => {
      const uploadStart = Date.now()

      try {
        await uploadRates({
          uploadParams: { rfpId, rateType: selectedRateType, file },
        })

        onFileUploaded()
      } catch {
        const uploadEnd = Date.now()
        const uploadDuration = uploadEnd - uploadStart

        if (uploadDuration > UPLOAD_TIMEOUT_IN_MS) {
          setUploadTimeoutError()
        }
      } finally {
        setIsUploadPricesFileDialogOpened(false)
      }
    },
    [rfpId, onFileUploaded, setUploadTimeoutError, uploadRates]
  )

  const handleDownloadRfpLanesTemplateFile = useCallback(async () => {
    try {
      await downloadRatesTemplate({ rfpId, rfpName })
    } catch {
      toast.error('Error downloading template. Please try again.')
    }
  }, [downloadRatesTemplate, rfpId, rfpName])

  return (
    <>
      {isUploadPricesFileDialogOpened && (
        <UploadRfpLanesDialog
          distanceUnit={distanceUnit}
          rateType={rateType}
          onUploadConfirm={handleOnUploadConfirm}
          onCancel={() => setIsUploadPricesFileDialogOpened(false)}
          isUploading={isUploading}
        />
      )}
      <Dropdown>
        <Dropdown.Trigger outlined={false}>
          <img src={IconGoogleSheets} alt="spread sheet" width="21" height="30" />
        </Dropdown.Trigger>
        <Dropdown.Menu>
          {/* @ts-expect-error - passing an async function to onClick will cause a type error */}
          <Dropdown.Item onClick={handleDownloadRfpLanesTemplateFile}>
            <Layout.Group gap="spacing-1" justify="flex-start" align="center">
              <Icon name="download" size="18" />
              Download Template
            </Layout.Group>
          </Dropdown.Item>
          {!isUploadDisabled && (
            <Dropdown.Item onClick={() => setIsUploadPricesFileDialogOpened(true)}>
              <Layout.Group gap="spacing-1" justify="flex-start" align="center">
                <Icon name="upload" size="18" />
                Upload Prices
              </Layout.Group>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </>
  )
}
